import React from 'react';
import I18n from '@/modules/i18n';
import moment from 'moment';
import { useQueryClient } from '@tanstack/react-query';
import {
  shared_program_lesson_assessment_score_path,
  unpublish_grade_shared_program_lesson_assessment_score_path,
} from '@/modules/routes';
import useExerciseResult from '@/components/staffer/Grading/useExerciseResult';
import { isAssignmentAutoGradeableOnly } from '@/components/student/AssignmentsBasedGradesTable/utils';
import { isAnswered } from '../utils';
import useGradingStore from '../useGradingStore';
import useIsStudentTransitioning from '../useIsStudentTransitioning';
import Status from './Status';
import StatusBoxSkeleton from './StatusBoxSkeleton';
import SubmissionScore, { useSubmissionScore } from './SubmissionScore';
import PostGradeButton from '../../../shared/Grading/Navigation/PostGradeButton';

export const formattedSubmittedAt = (submittedAt: string | null) => {
  if (!submittedAt) return '--';

  return moment(submittedAt).format('MMM D, hh:mm A z');
};

const StatusBox = () => {
  const queryClient = useQueryClient();

  const {
    assignment,
    isAssessment,
    isTeacher,
    programId,
    questionAnswers,
    showAnswers,
    student,
    submissionForStudent,
  } = useGradingStore();

  const { data: exerciseResult } = useExerciseResult();
  const isTransitioningStudent = useIsStudentTransitioning();

  const submission = submissionForStudent(student.id);
  const searchParams = new URLSearchParams(window.location.search);
  const classroomId = searchParams.get('classroom_id');
  const lessonId = searchParams.get('lesson_id');

  const score = useSubmissionScore();

  if (isTransitioningStudent) return <StatusBoxSkeleton />;
  if (!(student && questionAnswers && assignment && submission)) return null;

  const postGradeAndClearPath = () => (
    shared_program_lesson_assessment_score_path(
      programId,
      lessonId,
      student.id,
      {
        assessment_id: assignment.assignment_details.exercise_id,
        assignment_id: assignment.id,
        classroom_id: classroomId,
        format: 'json',
      },
    )
  );

  const regradeStudentPath = () => (
    unpublish_grade_shared_program_lesson_assessment_score_path(
      programId,
      lessonId,
      exerciseResult?.id || -1,
      {
        submission_id: submission.id,
      },
    )
  );

  const isGraded = !!exerciseResult?.graded_at;
  const readyToSubmit = submission.total_score_override || questionAnswers.filter(qa => !qa.score).length === 0;

  const isEditable = () => {
    if (!isTeacher) return false;

    if (isAssessment()) {
      if (submission?.submitted_at == null) return false;

      const allQuestionAutoGraded = assignment.questions.every(q => q.auto_gradable);

      if (allQuestionAutoGraded) return false;

      return !exerciseResult?.graded_at;
    }

    if (isAssignmentAutoGradeableOnly(assignment)) return false;

    return true;
  };

  const answerableQuestions = assignment.questions.filter(q => q.type !== 'PromptOnly');
  const answeredQuestionAnswers = questionAnswers.filter(isAnswered);

  return (
    <>
      <span className="tw-text-base tw-font-bold">{`${student.first_name} ${student.last_name}`}</span>
      <div className="tw-flex tw-justify-between tw-flex-col xl:tw-flex-row">
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <p className="tw-text-sm tw-font-bold tw-block tw-m-0">{I18n.t('status')}:</p>
          <Status />
          {showAnswers() && (
            <p className="tw-mb-0">
              {`${answeredQuestionAnswers.length}/${answerableQuestions.length} ${I18n.t('questions_answered')}`}
            </p>
          )}
          <div className="tw-mt-2">
            <p className="tw-text-sm tw-font-bold tw-block tw-m-0">{I18n.t('turned_in')}:</p>
            <p>{formattedSubmittedAt(submission.submitted_at)}</p>
          </div>
        </div>
        <div>
          <SubmissionScore
            key={score}
            editable={isEditable()}
            onChange={() => queryClient.invalidateQueries(
              ['submission', assignment.id.toString(), student.id.toString()],
            )}
          />
          {isTeacher && isAssessment() && (
            <PostGradeButton
              alreadyGraded={isGraded}
              assessmentTitle={assignment.assignment_details.title}
              clearWorkPath={postGradeAndClearPath()}
              exerciseResult={exerciseResult}
              exerciseType="Assessment"
              locale={I18n.locale}
              postGradePath={postGradeAndClearPath()}
              programId={programId}
              readyToSubmit={readyToSubmit}
              regradeStudentPath={regradeStudentPath()}
              studentName={`${student.first_name} ${student.last_name}`}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default StatusBox;
